import { Button, Text } from '@mantine/core';
import { useNavigate } from 'react-router-dom';
import { MIP_MANAGE_ROUTES } from '@dealstack/frontend/app/pages/Routing/routes';
import { mipManagePath } from '@dealstack/frontend/app/pages/Routing/routes.utils';
import { useRequiredParams } from '@dealstack/frontend/shared/utils/useRequiredParams';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import {
	FooterActionsRow,
	StepContainer,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Modals/SendDocumentsModal/SendDocumentsModal.styled';

interface Props {
	documentCount: number;
	recipientCount: number;
	onSendAnother: () => void;
	onClose: () => void;
}

/** Completion panel: send another batch, or jump to the programme's Documents tab to track these. */
export const SendSuccess = ({ documentCount, recipientCount, onSendAnother, onClose }: Props) => {
	const navigate = useNavigate();
	const { dealId, mipId } = useRequiredParams('dealId', 'mipId');
	const tMipManage = useTranslationMipManage();

	// Each noun is pluralised independently, then interpolated whole into the summary sentence —
	// a single `count` can only drive one plural choice, and this sentence needs two.
	const documentPart = tMipManage(
		documentCount === 1
			? 'send_documents.success.document_count_one'
			: 'send_documents.success.document_count_other',
		{ count: documentCount }
	);
	const recipientPart = tMipManage(
		recipientCount === 1
			? 'send_documents.success.recipient_count_one'
			: 'send_documents.success.recipient_count_other',
		{ count: recipientCount }
	);

	const handleTrackDocuments = () => {
		navigate(mipManagePath(MIP_MANAGE_ROUTES.DOCUMENTS, { dealId, mipId }));
		onClose();
	};

	return (
		<StepContainer data-testid="send-documents-success">
			<Text fw={600} size="lg">
				{tMipManage('send_documents.success.title')}
			</Text>
			<Text c="dark.0">
				{tMipManage('send_documents.success.summary', { documentPart, recipientPart })}
			</Text>
			<FooterActionsRow>
				<Button variant="outline" onClick={onSendAnother}>
					{tMipManage('send_documents.success.send_another')}
				</Button>
				<Button onClick={handleTrackDocuments}>
					{tMipManage('send_documents.success.track_documents')}
				</Button>
			</FooterActionsRow>
		</StepContainer>
	);
};
